import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  AppLayout,
  SideNavigation,
  HelpPanel,
  Header,
  SpaceBetween,
  TextContent,
  Flashbar,
  Grid,
  Badge,
  Button,
} from '@cloudscape-design/components';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useNotifications } from './NotificationContext';
import { useAuth } from '../AuthContext';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';
import { AUTH_API } from '../config/api';

const buildNavItems = (flags, isSuperAdmin) => {
  const items = [
    { type: 'link', text: 'Home', href: '/' },
    { type: 'link', text: 'Dashboard', href: '/dashboard' },
    { type: 'divider' },
    {
      type: 'section',
      text: 'Agents',
      items: [
        { type: 'link', text: 'Add Agent', href: '/Wizard' },
        { type: 'link', text: 'List Agents', href: '/agents' },
      ],
    },
    { type: 'divider' },
    {
      type: 'section',
      text: 'Workflows',
      info: !flags.workflow ? <Badge color="grey">Disabled</Badge> : undefined,
      items: [
        { type: 'link', text: 'Add Workflow', href: '/workflow/new' },
        { type: 'link', text: 'List Workflows', href: '/workflows' },
      ],
    },
    { type: 'divider' },
    {
      type: 'section',
      text: 'Knowledge Bases',
      info: !flags.knowledge_base ? <Badge color="grey">Disabled</Badge> : undefined,
      items: [
        { type: 'link', text: 'Add Knowledge Base', href: '/knowledge-base/new' },
        { type: 'link', text: 'List Knowledge Bases', href: '/knowledge-bases' },
        { type: 'link', text: 'Files', href: '/knowledge-base-files' },
      ],
    },
    { type: 'divider' },
    {
      type: 'section',
      text: 'Telephony',
      info: !flags.telephony ? <Badge color="grey">Disabled</Badge> : undefined,
      items: [
        { type: 'link', text: 'Add Telephony', href: '/telephony/new' },
        { type: 'link', text: 'List Telephony', href: '/telephony' },
      ],
    },
    { type: 'divider' },
    {
      type: 'section',
      text: 'Account',
      items: [
        { type: 'link', text: 'Billing & Credit', href: '/billing' },
        { type: 'link', text: 'Profile', href: '/profile' },
        { type: 'link', text: 'Company Settings', href: '/settings/company' },
        { type: 'link', text: 'Security', href: '/settings/security' },
      ],
    },
  ];

  if (isSuperAdmin) {
    items.push(
      { type: 'divider' },
      {
        type: 'section',
        text: 'Super Admin',
        items: [
          { type: 'link', text: 'API Management', href: '/admin/api-management' },
          { type: 'link', text: 'User Management', href: '/admin/users' },
          { type: 'link', text: 'LLM Configuration', href: '/admin/llm-config' },
          { type: 'link', text: 'SMTP Settings', href: '/admin/smtp-settings' },
        ],
      },
    );
  }

  return items;
};

const disabledHrefsForFlags = (flags) => {
  const disabled = new Set();
  if (!flags.workflow) {
    disabled.add('/workflow/new');
    disabled.add('/workflows');
  }
  if (!flags.knowledge_base) {
    disabled.add('/knowledge-base/new');
    disabled.add('/knowledge-bases');
    disabled.add('/knowledge-base-files');
  }
  if (!flags.telephony) {
    disabled.add('/telephony/new');
    disabled.add('/telephony');
  }
  return disabled;
};

export default function AppLayoutWrapper({
  children,
  breadcrumbs,
  header,
  headerBackgroundStyle,
  headerVariant = 'h1',
  helpPanelHeader = 'Help',
  helpPanelContent,
  splitPanel,
  toolsHide = false,
  disableContentPaddings = false,
  tools,
  toolsOpen: controlledToolsOpen,
  onToolsChange: controlledOnToolsChange,
  splitPanelOpen,
  onSplitPanelToggle,
  onSplitPanelPreferencesChange,
  onSplitPanelResize,
  splitPanelPreferences,
  splitPanelSize,
  content: customContent,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { items, removeNotification, addNotification, success: notifySuccess, error: notifyError } = useNotifications();
  const { user, checkAndHandleAuthCookies, isSuperAdmin } = useAuth();
  const { flags } = useFeatureFlags();
  const disabledHrefs = useMemo(() => disabledHrefsForFlags(flags), [flags]);
  const [isNavigationOpen, setIsNavigationOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [onboardingAlertShown, setOnboardingAlertShown] = useState(false);
  const [persistentNotifications, setPersistentNotifications] = useState([]);
  const [dismissedPersistent, setDismissedPersistent] = useState(new Set());

  const fetchPersistentNotifications = useCallback(async () => {
    const token = user?.access_token || user?.token;
    if (!token) return;
    try {
      const res = await axios.get('/api/notifications/?unread=true&limit=10', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.status === 'success') {
        setPersistentNotifications(res.data.data || []);
      }
    } catch (err) {
      // Silent fail — notifications are non-critical
    }
  }, [user]);

  useEffect(() => {
    fetchPersistentNotifications();
    const interval = setInterval(fetchPersistentNotifications, 60000);
    return () => clearInterval(interval);
  }, [fetchPersistentNotifications]);

  const handleDismissPersistent = useCallback(async (notifId) => {
    const token = user?.access_token || user?.token;
    setDismissedPersistent((prev) => new Set(prev).add(notifId));
    try {
      await axios.post(`/api/notifications/${notifId}/read/`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (err) {
      // Silent fail
    }
  }, [user]);

  const flashbarItems = useMemo(() => {
    const ephemeral = items.map((item) => ({
      ...item,
      dismissible: item.dismissible !== false,
      onDismiss: () => removeNotification(item.id),
    }));
    const persistent = persistentNotifications
      .filter((n) => !dismissedPersistent.has(n.id))
      .map((n) => ({
        id: `persistent-${n.id}`,
        type: n.type || 'info',
        header: n.header,
        content: n.content,
        dismissible: true,
        onDismiss: () => handleDismissPersistent(n.id),
        action: n.action_label && n.action_href ? (
          <Button href={n.action_href} onFollow={(e) => { e.preventDefault(); navigate(n.action_href); }}>
            {n.action_label}
          </Button>
        ) : undefined,
      }));
    return [...persistent, ...ephemeral];
  }, [items, removeNotification, persistentNotifications, dismissedPersistent, handleDismissPersistent, navigate]);

  const onboardingAllowedPaths = useMemo(
    () => ['/profile', '/settings/security', '/signin', '/signup', '/forgot-password'],
    []
  );

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (!user?.adexcloud_onboarding_pending) return;
    if (!onboardingAllowedPaths.includes(location.pathname)) {
      navigate('/profile', { replace: true });
      return;
    }
    if (onboardingAlertShown) return;

    const handleRetry = async () => {
      const token = user.access_token || user.token;
      try {
        const res = await fetch(AUTH_API.retryOnboarding, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (res.ok && data.success) {
          notifySuccess('Votre espace Adexcloud a été finalisé avec succès.');
          removeNotification('onboarding-pending');
          await checkAndHandleAuthCookies();
        } else {
          notifyError(data.error || 'La finalisation a échoué. Veuillez réessayer.');
        }
      } catch (err) {
        notifyError('Erreur réseau lors de la finalisation.');
      }
    };

    addNotification({
      id: 'onboarding-pending',
      type: 'warning',
      header: 'Finalisation du compte en attente',
      content: "La configuration de votre espace Adexcloud n'a pas pu être finalisée.",
      duration: null,
      dismissible: false,
      action: (
        <SpaceBetween direction="horizontal" size="xs">
          <Button onClick={handleRetry}>Réessayer</Button>
          <Button variant="link" onClick={() => navigate('/profile')}>
            Profil
          </Button>
        </SpaceBetween>
      ),
    });
    setOnboardingAlertShown(true);
  }, [
    user,
    onboardingAlertShown,
    onboardingAllowedPaths,
    location.pathname,
    addNotification,
    removeNotification,
    notifySuccess,
    notifyError,
    navigate,
    checkAndHandleAuthCookies,
  ]);

  const [isToolsOpen, setIsToolsOpen] = useState(false);

  const activeHref = location.pathname;
  const effectiveToolsOpen = controlledToolsOpen !== undefined ? controlledToolsOpen : isToolsOpen;
  const effectiveOnToolsChange = controlledOnToolsChange || (({ detail }) => setIsToolsOpen(detail.open));

  return (
    <>
      <AppLayout
      disableContentPaddings={disableContentPaddings || !!headerBackgroundStyle}
      navigationHide={false}
      toolsHide={toolsHide}
      navigationOpen={isNavigationOpen}
      onNavigationChange={({ detail }) => setIsNavigationOpen(detail.open)}
      toolsOpen={effectiveToolsOpen}
      onToolsChange={effectiveOnToolsChange}
      splitPanelOpen={splitPanelOpen}
      onSplitPanelToggle={onSplitPanelToggle}
      onSplitPanelPreferencesChange={onSplitPanelPreferencesChange}
      onSplitPanelResize={onSplitPanelResize}
      splitPanelPreferences={splitPanelPreferences}
      splitPanelSize={splitPanelSize}
      navigation={
        <SideNavigation
          activeHref={activeHref}
          header={{
            href: '/dashboard',
            logo: { alt: 'Adexgénie', src: '/adexgeniebot.png', width: isMobile ? '20%' : '25%' },
            text: (
              <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
                <span style={{ fontSize: '1.1rem', fontWeight: 700 }}>Adexgénie</span>
                {!isMobile && (
                  <span style={{ fontSize: '0.75rem', color: '#9ba3af' }}>Votre Chatbot en 5 Cliques</span>
                )}
              </span>
            ),
          }}
          onFollow={(event) => {
            if (!event.detail.external) {
              event.preventDefault();
              if (disabledHrefs.has(event.detail.href)) {
                return;
              }
              if (user?.adexcloud_onboarding_pending && !onboardingAllowedPaths.includes(event.detail.href)) {
                return;
              }
              navigate(event.detail.href);
            }
          }}
          items={buildNavItems(flags, isSuperAdmin)}
        />
      }
      tools={tools || (
        <HelpPanel header={<Header variant="h2">{helpPanelHeader}</Header>}>
          <SpaceBetween size="m">
            <TextContent>
              {helpPanelContent || (
                <SpaceBetween size="m">
                  <p>
                    <strong>Adexgénie</strong> is your AI workspace for customer engagement.
                    Use the side navigation to manage agents, knowledge bases, workflows, telephony, billing and account settings.
                  </p>
                  <ul>
                    <li><strong>Agents:</strong> Create and deploy voice or chat AI agents.</li>
                    <li><strong>Knowledge Bases:</strong> Upload documents to power agent answers.</li>
                    <li><strong>Workflows:</strong> Automate tasks with MCP and webhooks.</li>
                    <li><strong>Telephony:</strong> Connect Twilio or SIP trunks.</li>
                    <li><strong>Billing:</strong> Monitor balance, quota and purchases.</li>
                  </ul>
                  <p>
                    Need help? Open the floating assistant in the bottom-right corner.
                  </p>
                </SpaceBetween>
              )}
            </TextContent>
          </SpaceBetween>
        </HelpPanel>
      )}
      content={customContent || (
        <SpaceBetween size="l">
          {header && headerBackgroundStyle ? (
            <div
              style={{
                background: headerBackgroundStyle,
                padding: '0 56px 24px 32px',
                minHeight: '160px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                position: 'relative',
              }}
            >
              {breadcrumbs && (
                <div style={{ marginBottom: '8px' }}>
                  {breadcrumbs}
                </div>
              )}
              <Grid gridDefinition={[{ colspan: 8 }, { colspan: 4 }]}>
                <div>
                  <Header
                    variant={headerVariant}
                    actions={header.actions}
                    description={header.description}
                    info={header.info}
                  >
                    {header.title}
                  </Header>
                </div>
                <div className="adexgenie-header-brand" style={{ textAlign: 'right', paddingTop: '8px' }}>
                  <div style={{ fontSize: '2.2rem', fontWeight: 700, color: '#ffffff', lineHeight: 1.2, textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                    Adexgénie
                  </div>
                  <div style={{ fontSize: '0.9rem', color: '#d6f5ed', marginTop: '4px', textShadow: '0 1px 2px rgba(0,0,0,0.2)' }}>
                    Grow Your Business With AI
                  </div>
                </div>
              </Grid>
              {flashbarItems.length > 0 && (
                <div style={{ marginTop: '16px' }}>
                  <Flashbar items={flashbarItems} />
                </div>
              )}
            </div>
          ) : (
            <>
              {flashbarItems.length > 0 && (
                <Flashbar items={flashbarItems} />
              )}
              {header && (
                <Header
                  variant={headerVariant}
                  actions={header.actions}
                  description={header.description}
                  info={header.info}
                >
                  {header.title}
                </Header>
              )}
            </>
          )}
          {headerBackgroundStyle ? (
            <div style={{ padding: '0 32px' }}>
              {children}
            </div>
          ) : (
            children
          )}
        </SpaceBetween>
      )}
      splitPanel={splitPanel}
    />
    </>
  );
}
